/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { FormInstance, Modal } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CreateUser } from '@/client';
import { StatusText } from '@/components/StatusText';
import { useMessage } from '@/hooks/message';
import { useNewUserMutation } from '@/hooks/mutation/useNewUserMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';

export type InviteUserModalProps = {
  children: React.ReactNode;
  setSelectedRole: (role: string) => void;
  form: FormInstance<CreateUser>;
};

export function InviteUserModal({ children, setSelectedRole, form }: InviteUserModalProps) {
  const { t } = useMimicTranslation('users');
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [status, setStatus] = useState<'unconfirmed' | 'pending' | 'confirmed'>('unconfirmed');
  const [message] = useMessage();

  const closeModal = () => {
    setOpen(false);
  };

  const afterClose = () => {
    navigate(-1);
  };

  const afterSuccess = () => {
    setStatus('confirmed');
    setSelectedRole('editor');
    form.resetFields();

    setTimeout(() => {
      setStatus('unconfirmed');
    }, 2000);
  };

  const { mutate } = useNewUserMutation({
    onSuccess: () => {
      afterSuccess();
    },
    onError: (_, createUserDto) => {
      message.error(
        t('feedback.errorOnInvite', {
          user: `${createUserDto.firstName} ${createUserDto.lastName}`,
        }),
      );

      setStatus('unconfirmed');
    },
  });

  async function validateAndSubmit() {
    try {
      setStatus('pending');
      const userParams = await form.validateFields();
      mutate(userParams);
    } catch (error) {
      setStatus('unconfirmed');
    }
  }

  return (
    <Modal
      title={t('invite.title')}
      centered
      afterClose={afterClose}
      open={open}
      onOk={() => validateAndSubmit()}
      onCancel={closeModal}
      confirmLoading={status === 'pending'}
      okButtonProps={{ disabled: status === 'pending' }}
      cancelButtonProps={{ disabled: status === 'pending' }}
      okText={
        <StatusText
          status={status}
          text={{
            unconfirmed: t('invite.buttonTextUnconfirmed'),
            pending: t('invite.buttonTextPending'),
            confirmed: t('invite.buttonTextConfirmed'),
          }}
        />
      }
    >
      {children}
    </Modal>
  );
}
